import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress,
  Button, LinearProgress, Divider, Avatar, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import {
  Users, Briefcase, Activity, DollarSign, TrendingUp,
  UserCheck, UserPlus, Clock, XCircle, AlertCircle,
  BarChart3, CheckCircle2, ChevronRight,
  History, Settings, ShieldCheck, Mail, Plus,
  FileText, MessageSquare, CreditCard, PieChart, Sparkles
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts';

const StatCard = ({ title, value, icon, color = '#1E3A8A', onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      height: '100%',
      position: 'relative',
      borderBottom: `4px solid ${color}`,
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: `0 10px 20px ${color}25`
      } : {},
      transition: 'all 0.3s ease'
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [demoStats, setDemoStats] = useState({
      total_bookings: 0,
      pending_bookings: 0,
      confirmed_bookings: 0,
      completed_bookings: 0,
      cancelled_bookings: 0,
      bookings_today: 0,
      upcoming_bookings: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await api.get(
          '/super-admin/dashboard-analytics'
        );

        setData(response.data);
      } catch (error) {
        console.error(
          'Dashboard fetch error:',
          error
        );
      }

      try {
        const demoResponse = await api.get(
          '/demo-bookings/admin/stats'
        );

        setDemoStats(
          demoResponse.data?.data || {
            total_bookings: 0,
            pending_bookings: 0,
            confirmed_bookings: 0,
            completed_bookings: 0,
            cancelled_bookings: 0,
            bookings_today: 0,
            upcoming_bookings: 0,
          }
        );
      } catch (error) {
        console.error(
          'Demo stats fetch error:',
          error
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f8fafc' }}>
      <CircularProgress />
    </Box>
  );

  if (!data) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f8fafc' }}>
      <Typography variant="h6" color="error">Failed to load dashboard data. Please try again later.</Typography>
    </Box>
  );

  const { stats, trends, insights } = data;

  const quickActions = [
    { label: 'View Recruiter Requests', icon: <UserCheck size={18} />, path: '/super-admin/recruiters-requests', color: '#1E3A8A' },
    { label: 'Manage Users', icon: <Users size={18} />, path: '/super-admin/user-history', color: '#3B82F6' },
    { label: 'View Recruiter History', icon: <History size={18} />, path: '/super-admin/admin-history', color: '#1E3A8A' },
    { label: 'Platform Analytics', icon: <BarChart3 size={18} />, path: '/super-admin/analytics', color: '#10B981' },
    { label: 'View Jobs', icon: <Briefcase size={18} />, path: '/super-admin/jobs-history', color: '#F59E0B' },
    { label: 'View Interviews', icon: <Activity size={18} />, path: '/super-admin/interview-history', color: '#EC4899' },
    { label: 'View Support Tickets', icon: <MessageSquare size={18} />, path: '/allsupporttickets', color: '#64748B' },
    { label: 'Payment History', icon: <CreditCard size={18} />, path: '/super-admin/payments-history', color: '#06B6D4' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {/*<Typography variant="h1" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Super Admin Dashboard</Typography>*/}
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
          >
            Super Admin Dashboard</Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Platform-wide overview, recruiter management, and business growth insights.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<BarChart3 size={18} />}
            onClick={() => navigate('/super-admin/analytics')}
            sx={{ borderRadius: '12px', bgcolor: '#1E3A8A', textTransform: 'none', px: 3, py: 1, fontWeight: 600 }}
          >
            Detailed Analytics
          </Button>
        </Box>
      </Box>

            {/* Demo Booking Notifications */}
            {(demoStats.pending_bookings > 0 ||
              demoStats.confirmed_bookings > 0) && (
              <Box
                sx={{
                  mb: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Pending Demo Bookings */}
                {demoStats.pending_bookings > 0 && (
                  <Card
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #FCD34D",
                      bgcolor: "#FFFBEB",
                      boxShadow: "0 4px 12px rgba(245, 158, 11, 0.08)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 2.5,
                        "&:last-child": {
                          pb: 2.5,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: {
                            xs: "flex-start",
                            sm: "center",
                          },
                          justifyContent: "space-between",
                          gap: 2,
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "12px",
                              bgcolor: "#FEF3C7",
                              color: "#D97706",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <AlertCircle size={24} />
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#92400E",
                                fontSize: "1rem",
                              }}
                            >
                              {demoStats.pending_bookings} demo{" "}
                              {demoStats.pending_bookings === 1
                                ? "booking is"
                                : "bookings are"}{" "}
                              waiting for confirmation
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "#A16207",
                                mt: 0.5,
                              }}
                            >
                              Review pending demo requests and confirm
                              the schedule.
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          endIcon={<ChevronRight size={18} />}
                          onClick={() =>
                            navigate(
                              "/super-admin/scheduled-demos?status=PENDING"
                            )
                          }
                          sx={{
                            bgcolor: "#D97706",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "10px",
                            px: 2.5,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              bgcolor: "#B45309",
                            },
                          }}
                        >
                          View Bookings
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Confirmed Demo Bookings */}
                {demoStats.confirmed_bookings > 0 && (
                  <Card
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #86EFAC",
                      bgcolor: "#F0FDF4",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.08)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 2.5,
                        "&:last-child": {
                          pb: 2.5,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: {
                            xs: "flex-start",
                            sm: "center",
                          },
                          justifyContent: "space-between",
                          gap: 2,
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "12px",
                              bgcolor: "#DCFCE7",
                              color: "#16A34A",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <CheckCircle2 size={24} />
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#166534",
                                fontSize: "1rem",
                              }}
                            >
                              {demoStats.confirmed_bookings} confirmed{" "}
                              {demoStats.confirmed_bookings === 1
                                ? "demo is"
                                : "demos are"}{" "}
                              scheduled
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "#15803D",
                                mt: 0.5,
                              }}
                            >
                              View confirmed demo schedules and prepare
                              for upcoming meetings.
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          endIcon={<ChevronRight size={18} />}
                          onClick={() =>
                            navigate(
                              "/super-admin/scheduled-demos?status=CONFIRMED"
                            )
                          }
                          sx={{
                            bgcolor: "#16A34A",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "10px",
                            px: 2.5,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              bgcolor: "#15803D",
                            },
                          }}
                        >
                          View Confirmed Demos
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}

            {/* Main Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Users" value={stats.totalUsers} icon={<Users />} color="#1E3A8A" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Jobs" value={stats.totalJobs} icon={<Briefcase />} color="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Applications" value={stats.totalApplications} icon={<Activity />} color="#EC4899" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Revenue" value={`₹${Math.round(stats.totalRevenue)}`} icon={<DollarSign />} color="#10B981" />
        </Grid>
      </Grid>

      {/* Welcome & AI Insights + Performance Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: '24px', height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>Welcome Back, Super Admin!</Typography>
                <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.6, maxWidth: '800px' }}>
                  Monitor platform growth, manage recruiters, track revenue, oversee hiring activities, and ensure smooth platform operations from a centralized dashboard.
                </Typography>
              </Box>

              <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Sparkles size={20} color="#1E3A8A" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A', textTransform: 'uppercase', letterSpacing: '1px' }}>AI Insights</Typography>
                </Box>
                <Grid container spacing={2}>
                  {insights.map((insight, idx) => (
                    <Grid item xs={12} key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1E3A8A', mt: 1 }} />
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>{insight}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Quick Actions moved inside for better layout matching */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>Quick Actions</Typography>
                <Grid container spacing={2}>
                  {quickActions.map((action, idx) => (
                    <Grid item xs={6} sm={3} key={idx}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate(action.path)}
                        sx={{
                          py: 2,
                          px: 1,
                          borderRadius: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          borderColor: '#e2e8f0',
                          color: '#475569',
                          '&:hover': {
                            bgcolor: `${action.color}10`,
                            borderColor: action.color,
                            color: action.color
                          },
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      >
                        <Box sx={{ color: action.color }}>{action.icon}</Box>
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>Performance Metrics</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  {
                      label: 'Demo Bookings Today',
                      value: demoStats.bookings_today,
                      target: 10,
                      color: '#8B5CF6'
                  },
                  { label: 'New Recruiters Today', value: stats.newRecruitersToday, target: 10, color: '#1E3A8A' },
                  { label: 'Recruiter Approval Rate', value: `${stats.recruiterApprovalRate}%`, progress: stats.recruiterApprovalRate, color: '#10B981' },
                  { label: 'Jobs Posted Today', value: stats.jobsPostedToday, target: 50, color: '#3B82F6' },
                  { label: 'Applications Received Today', value: stats.applicationsReceivedToday, target: 200, color: '#EC4899' },
                  { label: 'Interviews Scheduled Today', value: stats.interviewsScheduledToday, target: 50, color: '#F59E0B' },
                  { label: 'Monthly Revenue', value: `₹${Math.round(stats.monthlyRevenue)}`, target: 100000, color: '#06B6D4' },
                  { label: 'Revenue Growth', value: `${stats.revenueGrowth >= 0 ? '+' : ''}${stats.revenueGrowth}%`, progress: Math.min(100, Math.max(0, 50 + stats.revenueGrowth)), color: stats.revenueGrowth >= 0 ? '#10B981' : '#EF4444' },
                  { label: 'Active Recruiters', value: stats.activeRecruiters, total: stats.totalRecruiters, color: '#1E3A8A' },
                  { label: 'Active Candidates', value: stats.activeCandidates, total: stats.totalCandidates, color: '#1E3A8A' },
                  { label: 'Support Tickets Pending', value: stats.pendingSupportTickets, target: 0, invert: true, color: '#F43F5E' },
                ].map((metric, i) => (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>{metric.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{metric.value}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={metric.progress !== undefined ? metric.progress : (metric.total ? (metric.value / metric.total) * 100 : Math.min(100, (metric.value / (metric.target || 1)) * 100))}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#f1f5f9',
                        '& .MuiLinearProgress-bar': { bgcolor: metric.color }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Platform Statistics Overview Cards */}
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 3, mt: 2 }}>Platform Statistics Overview</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: 'Total Recruiters', value: stats.totalRecruiters, color: '#1E3A8A' },
          { label: 'Approved Recruiters', value: stats.approvedRecruiterRequests, color: '#10B981' },
          { label: 'Pending Requests', value: stats.pendingRecruiterRequests, color: '#F59E0B' },
          { label: 'Rejected Recruiters', value: stats.rejectedRecruiterRequests, color: '#EF4444' },
          { label: 'Total Candidates', value: stats.totalCandidates, color: '#3B82F6' },
          { label: 'Active Jobs', value: stats.activeJobs, color: '#EC4899' },
          { label: 'Total Interviews', value: stats.totalInterviews, color: '#1E3A8A' },
          { label: 'Total Support Tickets', value: stats.totalSupportTickets, color: '#64748B' },
          { label: 'Monthly Revenue', value: `₹${Math.round(stats.monthlyRevenue)}`, color: '#06B6D4' },
          { label: 'Total Revenue', value: `₹${Math.round(stats.totalRevenue)}`, color: '#10B981' },
        ].map((stat, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={idx}>
            <Card sx={{ bgcolor: '#fff', borderRadius: '16px', border: '1px solid #f1f5f9', p: 2, height: '100%' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{stat.label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: stat.color, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>{stat.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Visual Analytics Section */}
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>Visual Analytics Section</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '24px', p: 3, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TrendingUp size={20} color="#1E3A8A" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Recruiter Growth Trend</Typography>
            </Box>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="colorRec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <ReTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="recruiters" stroke="#1E3A8A" fillOpacity={1} fill="url(#colorRec)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '24px', p: 3, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <DollarSign size={20} color="#10B981" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Revenue Analytics</Typography>
            </Box>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(val) => `₹${val}`} />
                  <ReTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fillOpacity={1} fill="url(#colorRev)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', p: 3, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Briefcase size={20} color="#3B82F6" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Job Posting Trend</Typography>
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <ReTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="jobs" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', p: 3, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Activity size={20} color="#EC4899" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Application Trend</Typography>
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <ReTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="applications" fill="#EC4899" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', p: 3, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <BarChart3 size={20} color="#F59E0B" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Interview Activity</Typography>
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <ReTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="applications" name="Interviews" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SuperAdminDashboard;
